import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Loader2, MessageCircleQuestion, Clock, CheckCircle, Trash2, Send, Pencil, ThumbsUp, Mail } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { questionsAPI } from '../services/api';

const fmt = (d) => new Date(d).toLocaleDateString('fr-FR', { day: 'numeric', month: 'short', year: 'numeric' });

const AnswerBox = ({ q, onChanged }) => {
  const [editing, setEditing] = useState(!q.answer);
  const [text, setText] = useState(q.answer?.message || '');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const save = async () => {
    if (text.trim().length < 2) { setError('Réponse trop courte'); return; }
    setBusy(true); setError('');
    try { await questionsAPI.answer(q.id, text); await onChanged(); setEditing(false); } catch (e) { setError(e.response?.data?.detail || 'Enregistrement impossible'); } finally { setBusy(false); }
  };
  if (!editing && q.answer) {
    return (
      <div className="mt-2 bg-orange-50/70 border-l-4 border-orange-400 rounded-r-lg p-3" data-testid={`admin-question-answer-${q.id}`}>
        <p className="text-xs font-semibold text-gray-900">Réponse publiée · {fmt(q.answer.updated_at || q.answer.created_at)}{q.notified_at && <span className="ml-2 inline-flex items-center gap-1 text-green-700 font-normal"><Mail size={11} /> client prévenu</span>}</p>
        <p className="text-sm text-gray-700 mt-1 whitespace-pre-line">{q.answer.message}</p>
        <button type="button" onClick={() => setEditing(true)} data-testid={`admin-question-edit-${q.id}`} className="mt-2 inline-flex items-center gap-1 text-xs text-blue-600 hover:underline"><Pencil size={12} /> Modifier la réponse</button>
      </div>
    );
  }
  return (
    <div className="mt-2" data-testid={`admin-question-answer-form-${q.id}`}>
      <textarea data-testid={`admin-question-answer-input-${q.id}`} value={text} onChange={(e) => setText(e.target.value)} rows={3} maxLength={2000} placeholder="Votre réponse publique…"
        className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-orange-500" />
      {error && <p className="text-xs text-red-600">{error}</p>}
      <div className="flex gap-2 mt-1.5">
        <Button size="sm" onClick={save} disabled={busy} data-testid={`admin-question-answer-submit-${q.id}`} className="bg-orange-500 hover:bg-orange-600">{busy ? <Loader2 size={14} className="mr-1.5 animate-spin" /> : <Send size={14} className="mr-1.5" />} {q.answer ? 'Enregistrer' : 'Publier la réponse'}</Button>
        {q.answer && <Button size="sm" variant="outline" onClick={() => { setEditing(false); setText(q.answer.message); }}>Annuler</Button>}
      </div>
    </div>
  );
};

const AdminQuestionsPanel = ({ onPendingChange }) => {
  const [data, setData] = useState({ questions: [], total: 0, pending: 0 });
  const [loading, setLoading] = useState(true);
  const [status, setStatus] = useState('pending');
  const [filter, setFilter] = useState('');
  const [busy, setBusy] = useState(null);

  const load = useCallback(async () => {
    try { const d = await questionsAPI.adminList(); setData(d); onPendingChange && onPendingChange(d.pending); } finally { setLoading(false); }
  }, [onPendingChange]);
  useEffect(() => { load(); }, [load]);

  const remove = async (q) => {
    if (!window.confirm('Supprimer définitivement cette question ?')) return;
    setBusy(q.id);
    try { await questionsAPI.remove(q.id); await load(); } finally { setBusy(null); }
  };

  const visible = data.questions.filter(q => (status === 'all' || q.status === status) &&
    (!filter || [q.product_name, q.author_name, q.email, q.question].some(v => (v || '').toLowerCase().includes(filter.toLowerCase()))));

  return (
    <>
      <h1 className="text-2xl font-bold text-gray-900 mb-6">Questions produits ({data.total})</h1>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
        {[
          { icon: MessageCircleQuestion, label: 'Questions au total', value: data.total, testid: 'questions-stat-total' },
          { icon: Clock, label: 'À répondre', value: data.pending, testid: 'questions-stat-pending', alert: data.pending > 0 },
          { icon: CheckCircle, label: 'Répondues (publiées)', value: data.total - data.pending, testid: 'questions-stat-answered' },
        ].map(s => (
          <div key={s.label} className={`bg-white rounded-lg shadow-sm p-4 flex items-center gap-4 ${s.alert ? 'ring-1 ring-amber-300' : ''}`}>
            <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${s.alert ? 'bg-amber-50 text-amber-600' : 'bg-orange-50 text-orange-500'}`}><s.icon size={20} /></div>
            <div><p className="text-xs text-gray-500">{s.label}</p><p className="text-lg font-bold text-gray-900" data-testid={s.testid}>{s.value}</p></div>
          </div>
        ))}
      </div>
      <div className="bg-white rounded-lg shadow-sm overflow-hidden">
        <div className="p-4 border-b flex flex-wrap items-center gap-3">
          <Input data-testid="questions-filter" value={filter} onChange={(e) => setFilter(e.target.value)} placeholder="Rechercher (produit, client, question)…" className="max-w-sm" />
          <div className="flex gap-1 ml-auto">
            {[['pending', `En attente${data.pending ? ` (${data.pending})` : ''}`], ['answered', 'Répondues'], ['all', 'Toutes']].map(([v, l]) => (
              <button key={v} data-testid={`questions-status-${v}`} onClick={() => setStatus(v)} className={`px-3 py-1.5 text-sm rounded-md ${status === v ? 'bg-orange-500 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}>{l}</button>
            ))}
          </div>
        </div>
        {loading ? <div className="flex justify-center py-12"><Loader2 className="animate-spin text-orange-500" /></div>
          : visible.length === 0 ? <p className="text-center py-10 text-gray-500" data-testid="questions-admin-empty">Aucune question</p>
          : (
            <ul className="divide-y" data-testid="questions-admin-list">
              {visible.map(q => (
                <li key={q.id} data-testid={`admin-question-${q.id}`} className={`p-4 flex gap-4 ${q.status === 'pending' ? 'bg-amber-50/40' : ''}`}>
                  <img src={q.product_image} alt="" className="w-14 h-14 object-contain bg-white border rounded flex-shrink-0" />
                  <div className="flex-1 min-w-0">
                    <div className="flex flex-wrap items-center gap-2">
                      <Link to={`/produit/${q.product_id}#questions`} className="font-medium text-gray-900 hover:text-orange-600 text-sm">{q.product_name}</Link>
                      <span className="text-xs text-gray-400">{q.product_brand}</span>
                      <span data-testid={`admin-question-status-${q.id}`} className={`text-xs px-2 py-0.5 rounded-full font-medium ${q.status === 'pending' ? 'bg-amber-100 text-amber-700' : 'bg-green-100 text-green-700'}`}>{q.status === 'pending' ? 'À répondre' : 'Publiée'}</span>
                      {q.helpful_count > 0 && <span className="text-xs text-gray-500 inline-flex items-center gap-1"><ThumbsUp size={11} /> {q.helpful_count}</span>}
                    </div>
                    <p className="text-sm text-gray-800 mt-1.5" data-testid={`admin-question-text-${q.id}`}>{q.question}</p>
                    <p className="text-xs text-gray-400 mt-1">{q.author_name} · <a href={`mailto:${q.email}`} className="hover:underline">{q.email}</a> · {fmt(q.created_at)}{q.user_id ? '' : ' · visiteur'}</p>
                    <AnswerBox q={q} onChanged={load} />
                  </div>
                  <div className="flex-shrink-0">
                    <button data-testid={`admin-question-delete-${q.id}`} onClick={() => remove(q)} disabled={busy === q.id} className="inline-flex items-center gap-1 px-2 py-1.5 text-xs rounded text-red-600 hover:bg-red-50 disabled:opacity-50"><Trash2 size={14} /> Supprimer</button>
                  </div>
                </li>
              ))}
            </ul>
          )}
      </div>
    </>
  );
};

export default AdminQuestionsPanel;
